import * as preact from "preact";

import type { GuideGlossaryEntry } from "stores/GuideStore";
import ReaderViewStore from "stores/ReaderViewStore";
import { positionWordPopUp } from "views/wordPopUpPositioning";

export interface GuideEntryPopUpOptions {
  entry: GuideGlossaryEntry;
  targetElement: JQuery<HTMLElement>;
  containerElement: JQuery<HTMLElement>;
  readerViewStore: ReaderViewStore;
  /**
   * Exactly one of these three runs, once, before the popup closes.
   * `onDismiss` also runs if the popup is removed externally (e.g. the user
   * clicks another word) so the word selection is always undone.
   */
  onDismiss: () => void;
  onTranslate: () => void;
  onOpenGuide: () => void;
}

/**
 * An entry's name as written in the text, its name in the guide's language
 * and its category, shared by the popup and the Guide tab
 */
export const GuideEntryTitle = ({ entry }: { entry: GuideGlossaryEntry }) => (
  <>
    {entry.term}
    {entry.translation && (
      <span class="guideEntryTranslation"> — {entry.translation}</span>
    )}
    {entry.type && <span class="guideEntryType">{entry.type}</span>}
  </>
);

interface ComponentProps extends GuideEntryPopUpOptions {
  mountEl: HTMLElement;
  onClose: () => void;
}

/**
 * Popover shown when the reader clicks a word matching a reading-guide
 * glossary entry: a quick who's-who reminder anchored to the word, with
 * escape hatches to the normal translate flow and to the full Guide tab.
 */
export class GuideEntryPopUpComponent extends preact.Component<ComponentProps> {
  private popUpRef = preact.createRef<HTMLDivElement>();
  private dismissAreaRef = preact.createRef<HTMLDivElement>();
  private disposePageLayoutUpdated?: () => void;
  private actionTaken = false;

  componentDidMount() {
    this.disposePageLayoutUpdated =
      this.props.readerViewStore.onPageLayoutUpdated(this.updatePosition);
    this.updatePosition();
    // The popup mounts on mouseup, so the opening gesture's mousedown has
    // already fired and can't dismiss it. Touch is covered by the reader's
    // touch-to-mouse event conversion.
    document.addEventListener("mousedown", this.onDocumentMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onDocumentMouseDown);
    this.disposePageLayoutUpdated?.();
    if (!this.actionTaken) {
      this.actionTaken = true;
      this.props.onDismiss();
    }
  }

  private onDocumentMouseDown = (event: MouseEvent) => {
    if (this.popUpRef.current?.contains(event.target as Node)) {
      return;
    }
    this.act(this.props.onDismiss);
  };

  private updatePosition = () => {
    positionWordPopUp({
      targetElement: this.props.targetElement,
      containerElement: this.props.containerElement,
      mountEl: this.props.mountEl,
      popUp: this.popUpRef.current,
      dismissArea: this.dismissAreaRef.current,
    });
  };

  private act = (action: () => void) => {
    if (this.actionTaken) {
      return;
    }
    this.actionTaken = true;
    action();
    this.props.onClose();
  };

  render() {
    const { entry } = this.props;
    return (
      <preact.Fragment>
        <div class="yourWordPopUp guideEntryPopUp" ref={this.popUpRef}>
          <div
            class="closeDialogButton"
            onClick={() => this.act(this.props.onDismiss)}
          >
            &nbsp;x&nbsp;
          </div>
          <h3>
            <GuideEntryTitle entry={entry} />
          </h3>
          {entry.aliases.length > 0 && (
            <p class="guideAliases">also: {entry.aliases.join(", ")}</p>
          )}
          <p>{entry.description}</p>
          <div class="guideEntryPopUpButtons">
            <button
              class="translateInstead"
              onClick={() => this.act(this.props.onTranslate)}
            >
              Translate
            </button>
            <button
              class="openGuide"
              onClick={() => this.act(this.props.onOpenGuide)}
            >
              <i class="icon-book"></i> Open Guide
            </button>
          </div>
        </div>
        <div
          class="dismissArea"
          ref={this.dismissAreaRef}
          onClick={() => this.act(this.props.onDismiss)}
        />
      </preact.Fragment>
    );
  }
}
